import React, { useState } from "react";
import { useAuth } from "@/lib/auth";
import { api, unwrap, errMsg } from "@/lib/api";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Card, CardContent } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { FileEdit, Award, ClipboardList, Clock, Check, Send } from "lucide-react";
import { formatDate } from "@/lib/utils";
import { useI18n } from "@/i18n";
import { guardianCopyFor } from "./guardianCopy";

export default function GuardianStudy() {
  const { user } = useAuth();
  const { t } = useI18n();
  const [activeWard, setActiveWard] = useState(user?.wards?.[0]?.id || "");
  const wards = user?.wards || [];

  return (
    <div className="space-y-5" data-testid="guardian-study">
      <div>
        <h1 className="font-display text-2xl font-bold text-[var(--text-primary)]">{t("guardian.studyTitle")}</h1>
        <p className="text-sm text-[var(--text-muted)] mt-1">{t("guardian.studySubtitle")}</p>
      </div>

      {wards.length > 1 && (
        <div className="flex gap-2 overflow-x-auto pb-1">
          {wards.map((w) => (
            <button
              key={w.id}
              onClick={() => setActiveWard(w.id)}
              data-testid={`ward-tab-${w.id}`}
              className={`flex-shrink-0 rounded-full px-4 py-1.5 text-sm font-semibold border transition-colors ${activeWard === w.id ? "bg-[var(--brand)] text-white border-[var(--brand)]" : "bg-white text-[var(--text-secondary)] border-[var(--subtle)]"}`}
            >
              {w.first_name}
            </button>
          ))}
        </div>
      )}

      <Tabs defaultValue="homework">
        <TabsList className="w-full">
          <TabsTrigger value="homework" className="flex-1" data-testid="tab-hw">{t("guardian.homework")}</TabsTrigger>
          <TabsTrigger value="exams" className="flex-1" data-testid="tab-exam">{t("guardian.exams")}</TabsTrigger>
          <TabsTrigger value="reports" className="flex-1" data-testid="tab-rep">{t("guardian.reportCards")}</TabsTrigger>
        </TabsList>
        <TabsContent value="homework"><HomeworkTab studentId={activeWard} /></TabsContent>
        <TabsContent value="exams"><ExamsTab studentId={activeWard} /></TabsContent>
        <TabsContent value="reports"><ReportsTab studentId={activeWard} /></TabsContent>
      </Tabs>
    </div>
  );
}

function HomeworkTab({ studentId }) {
  const { t, language } = useI18n();
  const copy = guardianCopyFor(language);
  const [open, setOpen] = useState(null);
  const { data, isLoading, refetch } = useQuery({
    queryKey: ["hw-guardian", studentId],
    queryFn: async () => unwrap(await api.get("/homework", { params: studentId ? { student_id: studentId } : {} })),
    enabled: !!studentId,
  });

  const today = new Date().toISOString().slice(0, 10);
  if (isLoading) return <Skeleton className="h-96 mt-4" />;

  return (
    <div className="space-y-3 mt-4 stagger">
      {(data || []).map((hw) => {
        const overdue = hw.due_date < today;
        const submitted = hw.my_submission?.status === "submitted" || hw.my_submission?.status === "graded";
        return (
          <Card key={hw.id} className="border-[var(--subtle)] lift-card cursor-pointer" onClick={() => setOpen(hw)} data-testid={`hw-${hw.id}`}>
            <CardContent className="p-4">
              <div className="flex items-start gap-3">
                <div className="h-10 w-10 rounded-md bg-[var(--brand)]/10 text-[var(--brand)] flex items-center justify-center flex-shrink-0">
                  <FileEdit className="h-4 w-4" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] uppercase tracking-widest text-[var(--text-muted)] font-semibold">{hw.subject?.name}</div>
                  <div className="font-semibold text-[var(--text-primary)] mt-0.5">{hw.title}</div>
                  <div className="mt-1.5 flex items-center gap-2 text-xs flex-wrap">
                    <Clock className="h-3 w-3 text-[var(--text-muted)]" />
                    <span className={overdue ? "text-[var(--error)] font-semibold" : "text-[var(--text-muted)]"}>{t("guardian.dueOn", { date: formatDate(hw.due_date) })}</span>
                    {submitted && <Badge className="bg-[var(--success-bg)] text-[var(--success)] border-0"><Check className="h-3 w-3 mr-0.5" /> {t("guardian.submitted")}</Badge>}
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        );
      })}
      {(data || []).length === 0 && (
        <Card className="border-[var(--subtle)]"><CardContent className="py-10 text-center text-[var(--text-muted)]">
          <FileEdit className="h-8 w-8 mx-auto mb-2 opacity-40" /> {t("guardian.noHomeworkYet")}
        </CardContent></Card>
      )}

      <Dialog open={!!open} onOpenChange={(v) => !v && setOpen(null)}>
      <DialogContent className="max-w-md" closeLabel={copy.close}>
          {open && <HomeworkDetail hw={open} studentId={studentId} onClose={() => { setOpen(null); refetch(); }} />}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function HomeworkDetail({ hw, studentId, onClose }) {
  const { t } = useI18n();
  const [text, setText] = useState(hw.my_submission?.text || "");
  const mut = useMutation({
    mutationFn: async () => unwrap(await api.post(`/homework/${hw.id}/submit`, { student_id: studentId, text, attachments: [] })),
    onSuccess: () => { toast.success(t("guardian.submitted")); onClose(); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const graded = hw.my_submission?.status === "graded";
  return (
    <div className="space-y-4">
      <DialogHeader>
        <DialogTitle className="font-display">{hw.title}</DialogTitle>
        <DialogDescription className="sr-only">{t("guardian.studySubtitle")}</DialogDescription>
      </DialogHeader>
      <div className="text-xs text-[var(--text-muted)]">{hw.subject?.name} · {t("guardian.dueOn", { date: formatDate(hw.due_date) })}</div>
      <div className="prose prose-sm max-w-none" dangerouslySetInnerHTML={{ __html: hw.description }} />
      {graded && (
        <div className="rounded bg-[var(--success-bg)] p-3">
          <div className="text-xs uppercase font-semibold text-[var(--success)] tracking-widest">{t("guardian.graded")}</div>
          <div className="font-mono-num font-bold text-lg">{hw.my_submission?.marks_obtained} / {hw.max_marks}</div>
          {hw.my_submission?.feedback && <div className="text-sm mt-1">{hw.my_submission.feedback}</div>}
        </div>
      )}
      <div>
        <div className="text-xs uppercase font-semibold text-[var(--text-secondary)] tracking-widest mb-2">{t("guardian.yourSubmission")}</div>
        <Textarea value={text} onChange={(e) => setText(e.target.value)} placeholder={t("guardian.submissionPlaceholder")} className="min-h-24 text-sm" data-testid="submit-text" />
      </div>
      <DialogFooter>
        <Button onClick={() => mut.mutate()} disabled={!text || mut.isPending} className="bg-[var(--accent)] hover:bg-[var(--accent-hover)] text-white" data-testid="submit-hw-btn">
          <Send className="h-4 w-4 mr-1.5" /> {mut.isPending ? t("guardian.submitting") : t("guardian.submit")}
        </Button>
      </DialogFooter>
    </div>
  );
}

function ExamsTab({ studentId }) {
  const { t } = useI18n();
  const { data: exams, isLoading } = useQuery({
    queryKey: ["exams-guardian"],
    queryFn: async () => unwrap(await api.get("/exams")),
  });
  const { data: results } = useQuery({
    queryKey: ["exam-results", studentId],
    queryFn: async () => unwrap(await api.get(`/students/${studentId}/exam-results`)),
    enabled: !!studentId,
  });
  if (isLoading) return <Skeleton className="h-96 mt-4" />;
  return (
    <div className="mt-4 space-y-4">
      <div>
        <div className="text-xs uppercase font-semibold text-[var(--text-secondary)] tracking-widest mb-2">{t("guardian.upcomingExams")}</div>
        <div className="space-y-2">
          {(exams || []).map((e) => (
            <Card key={e.id} className="border-[var(--subtle)]"><CardContent className="p-4">
              <div className="font-semibold text-sm">{e.name}</div>
              <div className="text-xs text-[var(--text-muted)]">{formatDate(e.start_date)} → {formatDate(e.end_date)}</div>
            </CardContent></Card>
          ))}
        </div>
      </div>

      <div>
        <div className="text-xs uppercase font-semibold text-[var(--text-secondary)] tracking-widest mb-2">{t("guardian.marks")}</div>
        <div className="space-y-2">
          {(results || []).map((m) => {
            const pct = m.max_marks ? Math.round((m.marks_obtained / m.max_marks) * 100) : 0;
            return (
              <Card key={m.id} className="border-[var(--subtle)]" data-testid={`result-${m.id}`}>
                <CardContent className="p-3 flex items-center gap-3">
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold text-sm truncate">{m.subject?.name}</div>
                    <div className="text-[10px] text-[var(--text-muted)] uppercase tracking-widest">{m.exam?.name}</div>
                  </div>
                  <div className="text-right">
                    <div className="font-mono-num font-bold text-lg">{m.marks_obtained}<span className="text-sm text-[var(--text-muted)] font-normal">/{m.max_marks}</span></div>
                    <div className="text-[10px] text-[var(--text-muted)]">{pct}%</div>
                  </div>
                </CardContent>
              </Card>
            );
          })}
          {(results || []).length === 0 && <div className="text-sm text-[var(--text-muted)]">{t("guardian.noMarksYet")}</div>}
        </div>
      </div>
    </div>
  );
}

function ReportsTab({ studentId }) {
  const { t, language } = useI18n();
  const copy = guardianCopyFor(language);
  const [open, setOpen] = useState(null);
  const { data, isLoading } = useQuery({
    queryKey: ["reports", studentId],
    queryFn: async () => unwrap(await api.get("/report-cards", { params: { student_id: studentId } })),
    enabled: !!studentId,
  });
  if (isLoading) return <Skeleton className="h-64 mt-4" />;
  return (
    <div className="mt-4 space-y-3 stagger">
      {(data || []).map((r) => (
        <Card key={r.id} className="border-[var(--subtle)] lift-card cursor-pointer" onClick={() => setOpen(r)} data-testid={`report-${r.id}`}>
          <CardContent className="p-4 flex items-center gap-3">
            <div className="h-10 w-10 rounded-md bg-[var(--accent)]/10 text-[var(--accent)] flex items-center justify-center flex-shrink-0">
              <Award className="h-5 w-5" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-semibold text-sm">{r.exam?.name}</div>
              <div className="text-[10px] text-[var(--text-muted)] uppercase tracking-widest">{r.overall_grade} · {r.percentage}%</div>
            </div>
            <div className="text-right">
              <div className="font-mono-num font-bold">{r.total_obtained}/{r.total_max}</div>
            </div>
          </CardContent>
        </Card>
      ))}
      {(data || []).length === 0 && (
        <Card className="border-[var(--subtle)]"><CardContent className="py-10 text-center text-[var(--text-muted)]">
          <Award className="h-8 w-8 mx-auto mb-2 opacity-40" /> {t("guardian.noReportCardsYet")}
        </CardContent></Card>
      )}

      <Dialog open={!!open} onOpenChange={(v) => !v && setOpen(null)}>
        <DialogContent className="max-w-lg" closeLabel={copy.close}>
          {open && (
            <div>
              <DialogHeader>
                <DialogTitle className="font-display">{t("guardian.reportCardTitle", { exam: open.exam?.name })}</DialogTitle>
                <DialogDescription className="sr-only">{t("guardian.studySubtitle")}</DialogDescription>
              </DialogHeader>
              <div className="mt-4 space-y-3">
                <div className="flex items-center justify-between rounded bg-[var(--brand)] text-white p-4">
                  <div>
                    <div className="text-[10px] uppercase tracking-widest opacity-70">{t("guardian.overallGrade")}</div>
                    <div className="font-display text-3xl font-bold">{open.overall_grade}</div>
                  </div>
                  <div className="text-right">
                    <div className="text-[10px] uppercase tracking-widest opacity-70">{t("guardian.percentage")}</div>
                    <div className="font-mono-num text-3xl font-bold">{open.percentage}%</div>
                  </div>
                </div>
                <table className="w-full text-sm border rounded overflow-hidden">
                  <thead className="bg-[var(--bg)]">
                    <tr>
                      <th className="text-left px-3 py-2 text-[11px] uppercase tracking-widest text-[var(--text-muted)]">{t("guardian.subject")}</th>
                      <th className="text-right px-3 py-2 text-[11px] uppercase tracking-widest text-[var(--text-muted)]">{t("guardian.marks")}</th>
                      <th className="text-left px-3 py-2 text-[11px] uppercase tracking-widest text-[var(--text-muted)]">{t("guardian.grade")}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(open.subjects || []).map((s, i) => (
                      <tr key={i} className="border-t border-[var(--subtle)]">
                        <td className="px-3 py-2 font-medium">{s.subject_name}</td>
                        <td className="px-3 py-2 text-right font-mono-num">{s.is_absent ? copy.absent : `${s.marks_obtained}/${s.max_marks}`}</td>
                        <td className="px-3 py-2 font-semibold">{s.grade}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
                {open.class_teacher_remark && (
                  <div className="rounded bg-[var(--bg)] p-3">
                    <div className="text-[10px] uppercase tracking-widest text-[var(--text-muted)] font-semibold">{t("guardian.teacherRemark")}</div>
                    <div className="text-sm mt-1">{open.class_teacher_remark}</div>
                  </div>
                )}
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
